import { Calendar, MapPin, Weight, Edit, Share2, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { ShareCatchDialog } from "./ShareCatchDialog";

interface CatchCardProps {
  catch: {
    id: string;
    fish_species: string;
    weight_lbs: number;
    date_caught: string;
    general_location: string;
    photo_url?: string;
    video_url?: string;
    status: string;
  };
  onClick?: () => void;
}

export const CatchCard = ({ catch: catchData, onClick }: CatchCardProps) => {
  const navigate = useNavigate();
  const statusColors = {
    pending: "warning",
    verified: "success",
    rejected: "destructive",
  } as const;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/edit-catch/${catchData.id}`);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      // Default behavior: navigate to public catch view
      navigate(`/catch/${catchData.id}`);
    }
  };

  return (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
      onClick={handleCardClick}
    >
      {catchData.photo_url && (
        <div className="aspect-video w-full overflow-hidden bg-muted">
          <img
            src={catchData.photo_url}
            alt={catchData.fish_species}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground">
              {catchData.fish_species}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={statusColors[catchData.status as keyof typeof statusColors] as any}>
              {catchData.status}
            </Badge>
            {catchData.status === "verified" && (
              <ShareCatchDialog catch={catchData} />
            )}
            {(catchData.status === "pending" || catchData.status === "verified") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEdit}
                className="h-8 w-8 p-0"
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Weight className="h-4 w-4 text-primary" />
          <span className="font-semibold text-foreground">{catchData.weight_lbs} lbs</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4 text-primary" />
          <span>{format(new Date(catchData.date_caught), "MMM d, yyyy")}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 text-primary" />
          <span>{catchData.general_location}</span>
        </div>
        {catchData.video_url && (
          <a
            href={catchData.video_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
            <span>Watch Video</span>
          </a>
        )}
      </CardContent>
    </Card>
  );
};